import { useEffect, useRef, useState } from 'react'
import { Formik, useFormikContext } from 'formik'
import { applyServerErrors, serverErrorsOf } from 'components/forms/serverErrors'
import { equals } from 'ramda'
import {
    Alert,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import { DeleteOutline, DragIndicator, EditOutlined } from '@mui/icons-material'
import MDButton from '../MDButton'
import { CONTACT_ADDRESS_TYPES, CONTACT_RESTORE_LABEL, labelOf } from './contactLabels'
import useDragReorder from './useDragReorder'
import PendingBadge from './PendingBadge'

const EMPTY_DRAFT = {
    type: CONTACT_ADDRESS_TYPES[0].value,
    city: '',
    street: '',
    number: '',
}

const FIELDS = [
    {
        name: 'street',
        label: 'רחוב',
    },
    {
        name: 'number',
        label: 'מספר',
    },
    {
        name: 'city',
        label: 'עיר',
    },
]

function fieldsFor(type) {
    return type === 'pobox'
        ? FIELDS.filter((field) => field.name !== 'street')
        : FIELDS
}

function useAddressDraft({ typeRef }) {
    const form = useFormikContext()
    const [editing, setEditing] = useState(null)
    const [focusRequests, setFocusRequests] = useState(0)

    useEffect(() => {
        if (focusRequests > 0) {
            typeRef.current?.focus()
        }
    }, [focusRequests])

    const draft = form.values

    const start = (id, loaded) => {
        setEditing({
            id,
            loaded,
        })
        form.resetForm({ values: loaded })
    }

    const reset = () => {
        setEditing(null)
        form.resetForm({
            values: {
                ...EMPTY_DRAFT,
                type: draft.type,
            },
        })
        setFocusRequests((count) => count + 1)
    }

    const returnRefused = ({ entered, editingId, loaded, fieldErrors }) => {
        setEditing(editingId == null
            ? null
            : {
                id: editingId,
                loaded,
            })
        form.setValues(entered, false)
        applyServerErrors(form, fieldErrors ?? {}, entered)
    }

    return {
        draft,
        setDraft: (next) => form.setValues(next, false),
        errors: serverErrorsOf(form.status, form.values),
        editingId: editing?.id ?? null,
        loaded: editing?.loaded ?? null,
        changed: editing != null && !equals(draft, editing.loaded),
        start,
        reset,
        returnRefused,
    }
}

export default function ContactAddressesSection(props) {
    return (
        <Formik
            initialValues={EMPTY_DRAFT}
            onSubmit={() => {}}
            validateOnChange={false}
            validateOnBlur={false}
        >
            <AddressesSectionBody {...props} />
        </Formik>
    )
}

function AddressesSectionBody({
    addresses = [],
    onAdd,
    onUpdate,
    onRemove,
    onReorder,
    onRestore,
    onConfirmRemoval,
    refusals = {},
    disabled = false,
    busy = false,
}) {
    const typeRef = useRef(null)
    const editor = useAddressDraft({ typeRef })
    const { draft, setDraft } = editor

    const locked = disabled || busy
    const reorderable = !locked && editor.editingId == null
    const { sorted, rowProps } = useDragReorder({
        rows: addresses,
        reorderable,
        onReorder,
    })

    const dragPropsOf = (address) => {
        const props = rowProps(address.id)

        if (address.storing !== true) {
            return props
        }

        return {
            ...props,
            draggable: false,
            onDragStart: undefined,
            onDrop: undefined,
        }
    }

    const submit = () => {
        const entered = draft
        const editingId = editor.editingId
        const loaded = editor.loaded
        const input = {
            type: draft.type,
            city: draft.city,
            street: draft.type === 'pobox' ? '' : draft.street,
            number: draft.number,
        }

        const outcome = editingId == null
            ? onAdd?.(input)
            : onUpdate?.({
                id: editingId,
                ...input,
            })

        editor.reset()

        Promise.resolve(outcome)
            .then((result) => {
                if (result?.refused == null) {
                    return
                }

                editor.returnRefused({
                    entered,
                    editingId,
                    loaded,
                    fieldErrors: result.fieldErrors,
                })
            })
    }

    const edit = (address) => {
        editor.start(address.id, {
            type: address.type,
            city: address.city ?? '',
            street: address.street ?? '',
            number: address.number ?? '',
        })
    }

    return (
        <Stack
            spacing={2}
            data-testid="contact-addresses-section"
            aria-busy={busy}
        >
            {sorted.length === 0 && (
                <Alert severity="info" data-testid="addresses-empty">לא הוגדרו כתובות</Alert>
            )}

            {sorted.map((address) => {
                const storing = address.storing === true
                const rowLocked = locked || storing

                return (
                    <Paper
                        key={address.id}
                        variant="outlined"
                        data-testid={`contact-address-${address.id}`}
                        data-id={address.id}
                        data-removed={address.removed === true ? 'true' : undefined}
                        {...dragPropsOf(address)}
                        sx={{
                            padding: 1,
                            ...(address.removed === true && { opacity: 0.6 }),
                        }}
                    >
                        <Stack
                            direction={{
                                xs: 'column',
                                lg: 'row',
                            }}
                            spacing={1}
                            alignItems={{ lg: 'center' }}
                        >
                            <DragIndicator
                                fontSize="small"
                                color={reorderable ? 'action' : 'disabled'}
                                sx={{ cursor: reorderable ? 'grab' : 'default' }}
                            />

                            <Typography variant="button" sx={{ minWidth: '11rem' }}>
                                {labelOf(CONTACT_ADDRESS_TYPES, address.type)}
                            </Typography>

                            <Typography variant="body2" sx={{ minWidth: '8rem' }}>{address.street}</Typography>

                            <Typography variant="body2" sx={{ minWidth: '8rem' }}>{address.number}</Typography>

                            <Typography variant="body2" sx={{ flexGrow: 1 }}>{address.city}</Typography>

                            {storing && (
                                <PendingBadge testId="contact-address-pending" />
                            )}

                            {address.removed === true && (
                                <>
                                    <MDButton
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        disabled={rowLocked}
                                        data-testid="address-confirm-removal"
                                        onClick={() => onConfirmRemoval?.(address.id)}
                                    >
                                        אישור מחיקה
                                    </MDButton>

                                    <MDButton
                                        variant="outlined"
                                        color="secondary"
                                        size="small"
                                        disabled={rowLocked}
                                        data-testid="address-restore"
                                        onClick={() => onRestore?.(address.id)}
                                    >
                                        {CONTACT_RESTORE_LABEL}
                                    </MDButton>
                                </>
                            )}

                            {address.removed !== true && (
                                <>
                                    <IconButton
                                        aria-label="עריכה"
                                        data-testid="address-edit"
                                        disabled={rowLocked || (editor.changed && editor.editingId !== address.id)}
                                        onClick={() => edit(address)}
                                    >
                                        <EditOutlined fontSize="small" />
                                    </IconButton>

                                    <IconButton
                                        aria-label="הסרה"
                                        data-testid="address-remove"
                                        disabled={rowLocked || editor.editingId === address.id}
                                        onClick={() => onRemove?.(address.id)}
                                    >
                                        <DeleteOutline fontSize="small" />
                                    </IconButton>
                                </>
                            )}

                            {refusals[address.id] != null && (
                                <Typography
                                    variant="caption"
                                    color="error"
                                    role="alert"
                                    data-testid="address-refusal"
                                >
                                    {refusals[address.id]}
                                </Typography>
                            )}
                        </Stack>
                    </Paper>
                )
            })}

            <Stack
                direction={{
                    xs: 'column',
                    lg: 'row',
                }}
                spacing={1}
                alignItems={{ lg: 'flex-start' }}
            >
                <TextField
                    id="contact-address-draft-type"
                    inputRef={typeRef}
                    label="סוג"
                    select
                    disabled={locked || editor.editingId != null}
                    value={draft.type}
                    onChange={(event) => setDraft({
                        ...draft,
                        type: event.target.value,
                    })}
                    slotProps={{
                        select: { native: true },
                        htmlInput: { 'data-testid': 'address-draft-type' },
                    }}
                    sx={{ minWidth: '11rem' }}
                >
                    {CONTACT_ADDRESS_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                </TextField>

                {fieldsFor(draft.type).map((field) => (
                    <TextField
                        key={field.name}
                        id={`contact-address-draft-${field.name}`}
                        label={field.label}
                        disabled={locked}
                        value={draft[field.name]}
                        error={editor.errors[field.name] != null}
                        helperText={editor.errors[field.name]}
                        onChange={(event) => setDraft({
                            ...draft,
                            [field.name]: event.target.value,
                        })}
                        slotProps={{
                            htmlInput: { 'data-testid': `address-draft-${field.name}` },
                            formHelperText: { 'data-testid': `address-draft-error-${field.name}` },
                        }}
                    />
                ))}

                <MDButton
                    variant="outlined"
                    color="info"
                    disabled={locked}
                    data-testid="address-draft-submit"
                    onClick={submit}
                >
                    {editor.editingId == null ? 'הוספה' : 'עדכון'}
                </MDButton>

                {editor.editingId != null && (
                    <MDButton
                        variant="outlined"
                        color="secondary"
                        onClick={editor.reset}
                    >
                        ביטול
                    </MDButton>
                )}
            </Stack>
        </Stack>
    )
}
